import { inject, Injectable } from '@angular/core';
import { AuthSession } from '@core/abstractions/auth-session';
import { SessionStorageService } from '@core/session-storage.service';
import { WINDOW } from '@core/window.service';
import {
  catchError,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import {
  AccessTokenCodeRequest,
  AccessTokenCodeResponse,
  AuthenticationLinkResponse,
  GstarClient,
  OAuthLoginSessionRequest,
  RefreshTokenRequest,
  RefreshTokenResponse,
  RevokeRefreshTokenRequest,
} from 'src/app/web-api-client';

const POST_LOGIN_REDIRECT_URL_KEY = 'postLoginRedirectUrl';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly gstarClient = inject(GstarClient);
  private readonly session = inject(SessionStorageService);
  private readonly window = inject<Window>(WINDOW);
  private automaticTokenRefreshSubscription?: Subscription;

  readonly automaticRefreshInterval: number = 10 * 60 * 1000; // 10 minutes

  readonly logout$ = new Subject<void>();

  readonly automaticTokenRefresh$ = timer(
    0,
    this.automaticRefreshInterval
  ).pipe(
    switchMap(() => this.regreshToken()),
    catchError(() => {
      this.preserveCurrentRouteForLogin();
      this.clearTokenSession();
      return this.getAuthenticationLink();
    })
  );

  get accessToken(): string {
    const sessionData: AuthSession = this.session.has('session')
      ? JSON.parse(this.session.get('session'))
      : ({} as AuthSession);

    return (sessionData && sessionData.accessToken) || '';
  }

  regreshToken(): Observable<RefreshTokenResponse> {
    const sessionData: AccessTokenCodeResponse = new AccessTokenCodeResponse(
      JSON.parse(this.getTokenSession())
    );
    const request = new RefreshTokenRequest({
      refreshToken: sessionData.refreshToken!,
      sessionId: sessionData.sessionId!,
    });
    return this.gstarClient.refreshtoken(request);
  }

  getAuthenticationLink = (): Observable<AuthenticationLinkResponse> =>
    this.gstarClient.authenticationlink();

  getAccessToken(
    request: AccessTokenCodeRequest
  ): Observable<AccessTokenCodeResponse> {
    return this.gstarClient.accesstoken(request);
  }

  completeOAuthLoginSession(
    request: OAuthLoginSessionRequest
  ): Observable<AccessTokenCodeResponse> {
    return this.gstarClient.oauthLoginsession(request);
  }

  logout(): Observable<void> {
    this.stopAutomaticTokenRefresh();

    if (!this.session.has('session')) {
      return of(undefined).pipe(tap(() => this.completeLogout()));
    }

    const session: AuthSession = JSON.parse(this.getTokenSession());
    const revokeRefreshTokenRequest = new RevokeRefreshTokenRequest({
      refreshToken: session.refreshToken,
      sessionId: session.sessionId,
    });
    return this.gstarClient.revoketoken(revokeRefreshTokenRequest).pipe(
      catchError(() => of(undefined)),
      tap(() => this.completeLogout())
    );
  }

  automaticTokenRefreshInitialize(): void {
    if (this.automaticTokenRefreshSubscription?.closed === false) {
      return;
    }

    this.automaticTokenRefreshSubscription =
      this.automaticTokenRefresh$.subscribe({
        next: (response: RefreshTokenResponse | AuthenticationLinkResponse) => {
          if (response instanceof RefreshTokenResponse) {
            this.saveTokenSession(response);
          } else if (
            response instanceof AuthenticationLinkResponse &&
            response?.url
          ) {
            this.window.location.href = response.url;
          }
        },
      });
  }

  getTokenSession = (): string => this.session.get('session');

  saveTokenSession = (response: RefreshTokenResponse): void =>
    this.session.set('session', JSON.stringify(response));

  clearTokenSession = (): void => this.session.remove('session');

  private stopAutomaticTokenRefresh(): void {
    this.automaticTokenRefreshSubscription?.unsubscribe();
    this.automaticTokenRefreshSubscription = undefined;
  }

  private completeLogout(): void {
    this.clearTokenSession();
    this.session.remove(POST_LOGIN_REDIRECT_URL_KEY);
    this.logout$.next();
  }

  private preserveCurrentRouteForLogin(): void {
    const { pathname, search, hash } = this.window.location;
    const redirectUrl = `${pathname}${search}${hash}`;

    if (redirectUrl.startsWith('/') && !redirectUrl.startsWith('//')) {
      this.session.set(POST_LOGIN_REDIRECT_URL_KEY, redirectUrl);
    }
  }
}
